import 'moment-timezone';

import * as localForage from 'localforage';

import { Injectable } from '@angular/core';
import { createStore } from '@ngneat/elf';
import { deleteAllEntities, getAllEntities, getEntity, upsertEntities, withEntities } from '@ngneat/elf-entities';
import {
	clearRequestsCache,
	createRequestsCacheOperator,
	createRequestsStatusOperator,
	initializeAsPending,
	updateRequestCache,
	updateRequestStatus,
	withRequestsCache,
	withRequestsStatus,
} from '@ngneat/elf-requests';
import { DefaultObject } from '@shared/interfaces';
import { CacheService } from '@shared/services';
import { persistState } from '@shared/third-party/persist-state/persist-state';

import { BaseRepository } from '../base.repository';
import { STORETIMEOUT } from '../db';

export interface InventoryTypes {
	id: string;
	isActive: boolean;
	inventoryTypeName: string;
	inventoryTypeId: string;
	priceSetting: DefaultObject[];
	productTypeId: number;
}

const store = createStore(
	{ name: 'inventoryTypes' },
	withEntities<InventoryTypes, 'inventoryTypeId'>({ idKey: 'inventoryTypeId' }),
	withRequestsStatus<`inventoryTypes`>(initializeAsPending('inventoryTypes')),
	withRequestsCache<`inventoryTypes`>()
);
export const trackInventoryTypesRequestsStatus = createRequestsStatusOperator(store);
export const persist = persistState(store, {
	key: 'inventoryTypes',
	storage: localForage,
});
export const skipWhileInventoryTypesCached = createRequestsCacheOperator(store);
setInterval(() => store.update(clearRequestsCache()), STORETIMEOUT);

@Injectable({ providedIn: 'root' })
export class InventoryTypesRepository extends BaseRepository {
	private excludedInventoryTypeIds: string[] = [];
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(private cache: CacheService) {
		super();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - store
	 **-------------------------------------------------------------------------------------
	 */
	get store() {
		return store;
	}

	get excludedInventoryTypes() {
		return this.excludedInventoryTypeIds;
	}

	set excludedInventoryTypes(excludedInventoryTypes) {
		this.excludedInventoryTypeIds = excludedInventoryTypes;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - queries
	 **-------------------------------------------------------------------------------------
	 */
	getAll = () => store.query(getAllEntities()).filter(e => e.isActive);
	get = id => store.query(getEntity(id));

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - add
	 **-------------------------------------------------------------------------------------
	 */
	public async add(items) {
		const companyId = await this.cache.get('secure:company_id');
		store.update(deleteAllEntities());
		store.update(
			upsertEntities(items.filter((e: any) => e.companyId == companyId)),
			updateRequestCache('inventoryTypes', { ttl: STORETIMEOUT }),
			updateRequestStatus('inventoryTypes', 'success')
		);
	}
}
